import React, { useState, useEffect, useCallback } from 'react';
import { streamStudentFile, triggerFileDownload } from '../../services/fileCabinetService';

const FILE_TYPE_STYLES = {
  IEP: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  evaluation: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  progress_report: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  accommodation: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  other: 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300',
};

const FILE_TYPE_LABELS = {
  IEP: 'IEP',
  evaluation: 'Evaluation',
  progress_report: 'Progress Report',
  accommodation: 'Accommodation',
  other: 'Other',
};

function formatFileSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return '—'; }
}

function resolveFileIds(file, studentId) {
  const sid = studentId ?? file?.student_id;
  const fid = file?.id ?? file?.student_file_id;
  if (sid == null || fid == null) return null;
  return { studentId: Number(sid), fileId: Number(fid) };
}

function buildPdfFragment(page, highlightQuote) {
  const parts = [];
  if (page) parts.push(`page=${page}`);
  const q = highlightQuote && String(highlightQuote).replace(/\s+/g, ' ').trim().slice(0, 120);
  if (q) parts.push(`search=${encodeURIComponent(q)}`);
  return parts.length ? `#${parts.join('&')}` : '';
}

function PreviewContent({ file, blobUrl, loadError, loading, isFullScreen, page, highlightQuote }) {
  const mime = file.mime_type || '';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <svg className="animate-spin w-6 h-6 text-indigo-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
      </div>
    );
  }

  if (loadError || !blobUrl) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <p className="text-gray-900 dark:text-white font-semibold">Could not load file</p>
        <p className="text-gray-500 dark:text-slate-400 text-sm mt-1">{loadError || 'Sign in required or file unavailable.'}</p>
      </div>
    );
  }

  if (mime === 'application/pdf') {
    const pdfUrl = `${blobUrl}${buildPdfFragment(page, highlightQuote)}`;
    return (
      <div className="flex flex-col h-full">
        {highlightQuote ? (
          <div className="mx-3 mt-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-950 dark:text-amber-100">
            <span className="font-semibold">Source quote: </span>
            <span className="font-mono">{String(highlightQuote).slice(0, 240)}</span>
            <span className="block text-[10px] text-amber-700 dark:text-amber-300/80 mt-1">
              The PDF viewer searches for this text when your browser supports it; otherwise use Ctrl+F on the page.
            </span>
          </div>
        ) : null}
        <iframe
          src={pdfUrl}
          title={file.file_name}
          className="w-full flex-1 border-0 rounded-lg"
          style={{ minHeight: isFullScreen ? 'calc(100vh - 180px)' : '55vh' }}
        />
      </div>
    );
  }

  if (mime.startsWith('image/')) {
    return (
      <div className="flex items-center justify-center h-full p-4 overflow-auto">
        <img
          src={blobUrl}
          alt={file.file_name}
          className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
          style={{ maxHeight: isFullScreen ? 'calc(100vh - 160px)' : '60vh' }}
        />
      </div>
    );
  }

  if (mime === 'text/plain' || mime === 'text/csv') {
    return <TextBlobPreview blobUrl={blobUrl} isFullScreen={isFullScreen} />;
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <p className="text-gray-900 dark:text-white font-semibold text-lg">Preview not available</p>
      <p className="text-gray-500 dark:text-slate-400 text-sm mt-1 max-w-sm">
        This file type cannot be previewed in the browser. Use download instead.
      </p>
    </div>
  );
}

function TextBlobPreview({ blobUrl, isFullScreen }) {
  const [textContent, setTextContent] = useState(null);

  useEffect(() => {
    fetch(blobUrl)
      .then((res) => res.text())
      .then(setTextContent)
      .catch(() => setTextContent('Failed to load file content.'));
  }, [blobUrl]);

  return (
    <div className="overflow-auto p-4" style={{ maxHeight: isFullScreen ? 'calc(100vh - 160px)' : '60vh' }}>
      <pre className="text-sm text-gray-800 dark:text-slate-200 font-mono whitespace-pre-wrap break-words bg-gray-50 dark:bg-slate-900 rounded-xl p-5 border border-gray-200 dark:border-slate-700">
        {textContent ?? 'Loading…'}
      </pre>
    </div>
  );
}

export default function DocumentViewer({ open, file, onClose, page, studentId, highlightQuote = null }) {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const ids = file ? resolveFileIds(file, studentId) : null;

  useEffect(() => {
    if (!open || !ids) {
      setBlobUrl(null);
      setLoadError(null);
      setLoading(false);
      return undefined;
    }

    let objectUrl;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setBlobUrl(null);

    streamStudentFile(ids.studentId, ids.fileId)
      .then(({ data }) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(data);
        setBlobUrl(objectUrl);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err.response?.data?.detail || err.message || 'Failed to load file';
        setLoadError(typeof msg === 'string' ? msg : 'Failed to load file');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, ids?.studentId, ids?.fileId]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      if (isFullScreen) setIsFullScreen(false);
      else onClose();
    }
  }, [isFullScreen, onClose]);

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, handleKeyDown]);

  const handleDownload = useCallback(() => {
    if (!ids) return;
    triggerFileDownload(ids.studentId, ids.fileId, file?.file_name);
  }, [ids, file?.file_name]);

  const handleOpenTab = useCallback(() => {
    if (!blobUrl) return;
    const tabUrl = file?.mime_type === 'application/pdf'
      ? `${blobUrl}${buildPdfFragment(page, highlightQuote)}`
      : blobUrl;
    window.open(tabUrl, '_blank', 'noopener,noreferrer');
  }, [blobUrl, page, highlightQuote, file?.mime_type]);

  if (!open || !file) return null;

  const canPreview = file.mime_type === 'application/pdf' ||
    file.mime_type?.startsWith('image/') ||
    file.mime_type === 'text/plain' ||
    file.mime_type === 'text/csv';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { if (isFullScreen) setIsFullScreen(false); else onClose(); }} />

      <div className={`relative bg-white dark:bg-slate-800 shadow-2xl border border-gray-200 dark:border-slate-700 flex flex-col transition-all duration-300 ${
        isFullScreen
          ? 'fixed inset-0 rounded-none'
          : 'rounded-2xl w-full max-w-5xl mx-4 max-h-[90vh]'
      }`}>
        <div className="flex items-center gap-3 px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-slate-700 flex-shrink-0">
          <div className="flex-1 min-w-0 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-600/20 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">{file.file_name}</h3>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${FILE_TYPE_STYLES[file.file_type] || FILE_TYPE_STYLES.other}`}>
                  {FILE_TYPE_LABELS[file.file_type] || file.file_type}
                </span>
                <span className="text-gray-400 dark:text-slate-500 text-xs">{formatFileSize(file.file_size)}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            {canPreview && blobUrl && (
              <button
                type="button"
                onClick={handleOpenTab}
                className="p-2 rounded-xl text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                title="Open in new tab"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </button>
            )}
            {ids && (
              <button
                type="button"
                onClick={handleDownload}
                className="p-2 rounded-xl text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                title="Download"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsFullScreen((prev) => !prev)}
              className="hidden sm:flex p-2 rounded-xl text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              title={isFullScreen ? 'Exit full screen' : 'Full screen'}
            >
              {isFullScreen ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                </svg>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-xl text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              title="Close"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-gray-50 dark:bg-slate-900">
          <PreviewContent
            file={file}
            blobUrl={blobUrl}
            loadError={loadError}
            loading={loading}
            isFullScreen={isFullScreen}
            page={page}
            highlightQuote={highlightQuote}
          />
        </div>

        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 px-4 sm:px-6 py-3 border-t border-gray-100 dark:border-slate-700 text-xs text-gray-500 dark:text-slate-400 flex-shrink-0">
          {file.created_by?.name && (
            <span className="flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              {file.created_by.name}
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {formatDate(file.created_at)}
          </span>
          {file.description && (
            <span className="flex items-center gap-1.5 truncate max-w-xs">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
              {file.description}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
